/**
 * Single-page navigation helpers.
 *
 * ACSolar is a one-page site: every nav target is an in-page anchor except the
 * legal pages (privacy, cookies, terms), which are real routes. WordPress menu
 * items therefore carry either `#anchor` or `/route` in `link.url`, and the
 * Header/Footer need to render each kind differently — a Gatsby <Link> to
 * "#solucoes" would try to navigate to a page called "#solucoes".
 */

/** True when a menu URL points at a section on the current page. */
export const isAnchor = (url) =>
  typeof url === "string" && url.trim().startsWith("#")

/** Strip the leading "#" — "#solucoes" → "solucoes". */
export const anchorId = (url) => (isAnchor(url) ? url.trim().slice(1) : "")

/**
 * "#top" (and a bare "#") mean the top of the page rather than a section.
 * The first block is the hero, and giving it a visible kicker just to create an
 * anchor would print "Início" above the headline — which the design does not have.
 */
export const isTopAnchor = (url) => {
  const id = anchorId(url).toLowerCase()
  return isAnchor(url) && (id === "" || id === "top" || id === "inicio")
}

/**
 * Slug used as the DOM id for a CMS block, so menu anchors can target it.
 * Prefers the editor-controlled kicker, falls back to the title, then to the
 * block type. Keep in sync with the anchors stored in the WP menu.
 */
export const sectionId = (source, fallback = "") => {
  const raw = (source || fallback || "").toString().trim()
  if (!raw) return ""
  return raw
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // strip accents: "Soluções" → "Solucoes"
    .toLowerCase()
    .replace(/&/g, " e ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

/**
 * Smooth-scroll helpers.
 *
 * These animate with requestAnimationFrame rather than
 * `window.scrollTo({ behavior: "smooth" })`. Native smooth scrolling is not
 * dependable — it is a no-op in some embedded/automated browsers and can be
 * interrupted mid-flight by a competing scroll, which left the nav appearing to
 * do nothing. A manual tween behaves identically everywhere and lets us honour
 * prefers-reduced-motion explicitly.
 */
const DURATION_MS = 500

// easeInOutCubic
const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches)

const animateScrollTo = (targetY) => {
  if (typeof window === "undefined") return

  const maxY = Math.max(
    0,
    document.documentElement.scrollHeight - window.innerHeight,
  )
  const to = Math.max(0, Math.min(targetY, maxY))
  const from = window.pageYOffset

  if (prefersReducedMotion() || Math.abs(to - from) < 2) {
    window.scrollTo(0, to)
    return
  }

  const start = performance.now()

  const step = (now) => {
    const progress = Math.min((now - start) / DURATION_MS, 1)
    window.scrollTo(0, from + (to - from) * ease(progress))
    if (progress < 1) window.requestAnimationFrame(step)
  }

  window.requestAnimationFrame(step)
}

/**
 * Scroll an in-page section into view, offset by the fixed header so the
 * heading is not hidden underneath it.
 */
export const scrollToSection = (id, headerOffset = 72) => {
  if (typeof document === "undefined") return false

  const target = document.getElementById(id)
  if (!target) return false

  const top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset
  animateScrollTo(top)

  // Keep the URL shareable without triggering a second, native jump.
  if (window.history?.replaceState) {
    window.history.replaceState(null, "", `#${id}`)
  }
  return true
}

/** Scroll back to the very top (the "Início" nav item). */
export const scrollToTop = () => {
  if (typeof window === "undefined") return
  animateScrollTo(0)
  if (window.history?.replaceState) {
    window.history.replaceState(null, "", window.location.pathname)
  }
}
